import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { SETUP_FADE_MS, SETUP_SLIDE_EASE, SETUP_SLIDE_MS, useStepPhase } from './setupStepPhase';

/**
 * Contenido de un paso del cuestionario "Account setup" (ADR-729, slide ADR-736).
 * El **header** (back + barra de progreso + "N de N") ya **no** vive acá: lo dibuja
 * fijo el `AccountSetupLayout`. Este componente arma las dos capas que se animan:
 *
 * - **Contenido**: título + items scrolleables. En **mobile** entra/sale con
 *   **slide/push** horizontal; en **desktop (`md:`)** con un **fade** (cross-dissolve),
 *   por pedido (ADR-752).
 * - **Footer (CTA)**: en mobile hace **fade in/out** sobre un gradiente; en desktop
 *   queda **fijo** con un **divisor** arriba y el botón a la derecha (referencia estilo
 *   Duolingo). Al no atenuarse y ser opaco, tapa al footer de la pantalla saliente →
 *   el botón se ve fijo mientras el contenido hace fade.
 *
 * Los **fades de scroll** (Figma node 39:1380) se mantienen sólo en mobile.
 *
 * Se monta **absoluto** (`inset-0`) porque durante la transición el layout tiene
 * montadas dos pantallas apiladas.
 */
export interface SetupScaffoldProps {
  /** Contenido scrolleable: normalmente el `<h1>` + los items del paso. */
  children: ReactNode;
  /** Acción fija al pie (ej. el botón "Siguiente"). Si se omite, no hay footer. */
  footer?: ReactNode;
}

/**
 * Fade de borde para el scroll: gradiente blanco + blur **progresivo** (0→2px,
 * `backdrop-filter` enmascarado; la máscara lo desvanece del borde hacia adentro).
 * `side` define hacia qué borde se intensifica; `whiteStop` es hasta qué % llega el
 * blanco del gradiente (100% arriba, 40% abajo).
 */
function ScrollFade({
  side,
  whiteStop = 100,
  soft = false,
  className,
}: {
  side: 'top' | 'bottom';
  whiteStop?: number;
  /** Rampa suavizada (eased): arranca casi transparente del lado del contenido para
   *  que el borde con lo nítido no se sienta abrupto (se usa en el fade superior). */
  soft?: boolean;
  className?: string;
}) {
  const dir = side === 'top' ? 'to top' : 'to bottom';
  const white = soft
    ? `linear-gradient(${dir}, rgba(255,255,255,0) 0%, rgba(255,255,255,0.12) 45%, rgba(255,255,255,0.55) 74%, #ffffff 100%)`
    : `linear-gradient(${dir}, rgba(255,255,255,0) 0%, #ffffff ${whiteStop}%)`;
  const mask = soft
    ? `linear-gradient(${dir}, rgba(0,0,0,0) 0%, rgba(0,0,0,0.12) 45%, rgba(0,0,0,0.55) 74%, #000 100%)`
    : `linear-gradient(${dir}, rgba(0,0,0,0) 0%, #000 100%)`;
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-x-0 ${className ?? ''}`}>
      {/* Blur progresivo 0→2px (backdrop-blur enmascarado). */}
      <div
        className="absolute inset-0"
        style={{
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          maskImage: mask,
          WebkitMaskImage: mask,
        }}
      />
      {/* Gradiente blanco. */}
      <div className="absolute inset-0" style={{ background: white }} />
    </div>
  );
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** ≥768px = "desktop" del cuestionario (coincide con el breakpoint `md:` de las clases). */
const DESKTOP_QUERY = '(min-width: 768px)';

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(DESKTOP_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

export function SetupScaffold({ children, footer }: SetupScaffoldProps) {
  const { phase, dir } = useStepPhase();
  const reduce = prefersReducedMotion();
  const isDesktop = useIsDesktop();

  // La pantalla que entra arranca oculta (corrida o transparente) y en el próximo
  // frame va a su estado de reposo, así el navegador anima. La que sale parte de
  // reposo y va al estado oculto.
  const [entered, setEntered] = useState(phase !== 'enter');
  useEffect(() => {
    if (phase === 'enter') {
      setEntered(false);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
    setEntered(true);
  }, [phase]);

  const hidden = phase === 'leave' || (phase === 'enter' && !entered);

  // Contenido: desktop = fade (opacity); mobile = slide (translateX push).
  const enterFrom = dir === 'forward' ? '100%' : '-100%';
  const leaveTo = dir === 'forward' ? '-100%' : '100%';
  const translateX = hidden ? (phase === 'leave' ? leaveTo : enterFrom) : '0%';

  const contentStyle: CSSProperties = reduce
    ? {}
    : isDesktop
      ? { opacity: hidden ? 0 : 1, transition: `opacity ${SETUP_FADE_MS}ms ease-out` }
      : {
          transform: `translateX(${translateX})`,
          transition: `transform ${SETUP_SLIDE_MS}ms ${SETUP_SLIDE_EASE}`,
          willChange: 'transform',
        };

  // Footer: en mobile hace fade in/out; en desktop queda fijo (opacity 1) — al ser
  // opaco y estar la pantalla entrante por encima, tapa al footer saliente.
  const footerFades = !isDesktop && !reduce;
  const footerOpacity = isDesktop ? 1 : hidden ? 0 : 1;

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Capa de contenido. */}
      <div className="absolute inset-0 flex flex-col" style={contentStyle}>
        <div className="relative min-h-0 flex-1 overflow-y-auto">
          {/* Fade superior de scroll (sólo mobile). */}
          <div className="pointer-events-none sticky top-0 z-20 -mb-8 h-8 md:hidden">
            <ScrollFade side="top" soft className="inset-0" />
          </div>

          <div className={`flex flex-col gap-10 px-5 pt-8 md:px-8 md:pt-10 ${footer ? 'pb-32 md:pb-40' : 'pb-8'}`}>
            {children}
          </div>
        </div>
      </div>

      {/* Capa de footer. */}
      {footer ? (
        <div
          // `fixed` en TODOS los tamaños: el footer se ancla al borde inferior del
          // viewport, no al del contenedor. Puede hacerlo porque ningún ancestro tiene
          // `transform` (la capa que sí lo tiene —el slide del contenido— es su hermana,
          // no su padre), y así escapa del contenedor de 800px y sus `overflow-hidden`:
          // en desktop eso es lo que hace que el divisor (`md:border-t`) llegue de borde
          // a borde con el botón a la derecha.
          //
          // En mobile era `absolute`, o sea que colgaba del alto del contenedor
          // (`--app-height`). Cuando esa medida se quedaba corta —el bug del acceso
          // directo de iOS— el botón quedaba flotando sobre una franja blanca en vez de
          // pegado al borde. Con `fixed` el pie deja de depender de esa medida: se apoya
          // en el viewport, que es lo que el usuario efectivamente ve, tanto en la PWA
          // instalada como en el navegador (donde queda arriba de la barra de URL, no
          // debajo). Ver ADR-2026-08-03-footer-del-cuestionario-anclado-al-viewport.
          className="fixed inset-x-0 bottom-0 z-20"
          style={{
            opacity: footerOpacity,
            transition: footerFades ? `opacity ${SETUP_FADE_MS}ms ease-out` : undefined,
          }}
        >
          {/* Gradiente (sólo mobile); en desktop el footer es opaco con divisor. */}
          <ScrollFade side="bottom" whiteStop={40} className="bottom-0 h-32 md:hidden" />
          {/* Barra full-width (divisor de borde a borde + fondo). */}
          <div className="relative px-5 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-4 md:border-t md:border-[#E4E4E7] md:bg-white md:px-0 md:pb-8 md:pt-5">
            {/* El BOTÓN se alinea con el contenedor principal (800px centrado): termina
                justo donde terminan las cards (mismo `max-w-[800px]` + `px-8`). */}
            <div className="md:mx-auto md:flex md:max-w-[800px] md:justify-end md:px-8">
              {footer}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default SetupScaffold;
